import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import LandingNav from './LandingNav';
import LandingForm from './LandingForm';
import FitCheck from './FitCheck';
import LandingRings, { CTA_RINGS, HERO_NODES, HERO_RINGS, SERVE_RINGS } from './LandingRings';
import HowWeWork from './HowWeWork';
import LandingFaq from './LandingFaq';
import LandingFooter from './LandingFooter';
import {
 EBS_OWNS,
 FIT_SIGNALS,
 NEXT_STEPS,
 PROVIDERS_OWN,
 SECTORS,
 STATS,
 TICKER
} from './landingContent';
import { getContactEmail } from '../../utils/contact';
import markOval from '../../assets/images/landing/mark-oval.svg';
import markHighlighter from '../../assets/images/landing/mark-highlighter.svg';
import clientHub from '../../assets/images/landing/client-hub.png';
import takingNote from '../../assets/images/landing/taking-note.svg';
import iconCalendar from '../../assets/images/landing/icon-calendar-check.svg';
import iconFolder from '../../assets/images/landing/icon-folder-slash.svg';
import iconList from '../../assets/images/landing/icon-list-check.svg';
import iconClipboard from '../../assets/images/landing/icon-clipboard-check.svg';
import heroPeopleOffice from '../../assets/images/landing/hero-people-office.png';

/** Ticker items repeated so one copy outruns any viewport; the marquee loops at -50%. */
const TICKER_RUN = [...TICKER, ...TICKER, ...TICKER];

const EXPECTATIONS = [
 { icon: iconCalendar, title: '30-45 minutes', detail: 'Focused, executive-level' },
 { icon: iconFolder, title: 'No system access needed', detail: 'Nothing to prepare or share' },
 { icon: iconList, title: 'What we cover', detail: 'Priorities, renewals, risk, providers' },
 { icon: iconClipboard, title: 'What you leave with', detail: 'A clear recommended next step' }
];

const SECTOR_SHAPES = {
 petals: ['petal p1', 'petal p2', 'petal p3'],
 cross: ['bar v', 'bar h'],
 stack: ['tile t1', 'tile t2', 'tile t3', 'tile t4']
};

function useReveal(rootRef) {
 useEffect(() => {
 const root = rootRef.current;
 if (!root) {
 return undefined;
 }
 const nodes = Array.from(root.querySelectorAll('.lp-reveal'));
 const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
 if (reduce || !('IntersectionObserver' in window)) {
 nodes.forEach((node) => node.setAttribute('data-in', ''));
 return undefined;
 }
 const observer = new IntersectionObserver(
 (entries) => {
 entries
 .filter((entry) => entry.isIntersecting)
 .forEach((entry, order) => {
 entry.target.style.transitionDelay = `${order * 60}ms`;
 entry.target.setAttribute('data-in', '');
 observer.unobserve(entry.target);
 });
 },
 { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
 );
 nodes.forEach((node) => observer.observe(node));
 return () => observer.disconnect();
 }, [rootRef]);
}

const LandingPage = () => {
 const rootRef = useRef(null);
 const [fitSelected, setFitSelected] = useState([]);
 const email = getContactEmail();
 useReveal(rootRef);

 const toggleFit = (index) => {
 setFitSelected((prev) =>
 prev.includes(index) ? prev.filter((item) => item !== index) : [...prev, index].sort()
 );
 };
 const fitChips = fitSelected.map((index) => FIT_SIGNALS[index].chip);

 return (
 <div className="lp" ref={rootRef}>
 <header className="lp-hero" id="top">
 <div className="lp-hero-art" aria-hidden="true">
 <span className="lp-glow lp-hero-glow-navy" />
 <span className="lp-glow lp-hero-glow-cyan" />
 <LandingRings
 className="lp-hero-rings"
 rings={HERO_RINGS}
 core={{ r: 10, color: '#00afd7', opacity: 0.9 }}
 nodes={HERO_NODES}
 />
 </div>
 <LandingNav />
 <div className="lp-wrap lp-hero-grid">
 <div className="lp-hero-copy">
 <p className="lp-badge"><i aria-hidden="true" />vCIO advisory · Technology sourcing</p>
 <h1>
 Technology leadership,{' '}
 <br />
 without the{' '}
 <span className="lp-hero-accent">
 bigger
 <img src={markOval} alt="" aria-hidden="true" />
 </span>{' '}
 <br />
 IT department.
 </h1>
 <p className="lp-hero-lede">
 EBS gives healthcare, nonprofit, and manufacturing leaders a senior technology partner - roadmaps, risk, and provider decisions - so you invest with confidence instead of guesswork.
 </p>
 <div className="lp-hero-actions">
 <a className="lp-btn lp-btn-primary lp-btn-lg" href="#contact">
 Book a Strategy Review <span className="lp-btn-disc" aria-hidden="true">→</span>
 </a>
 <a className="lp-btn lp-btn-ghost lp-btn-lg" href="#how-we-work">See how we work</a>
 </div>
 <p className="lp-meta">30-45 min · No system access needed · Clear next steps</p>
 </div>

 <figure className="lp-hero-photo">
 <div className="lp-hero-photo-glow" aria-hidden="true" />
 <div className="lp-hero-photo-frame">
 <img
 src={heroPeopleOffice}
 alt="Technology advisors collaborating around a laptop in the office"
 width="1400"
 height="1400"
 decoding="async"
 />
 </div>
 <div className="lp-hero-photo-accent" aria-hidden="true">
 <span className="lp-hero-photo-accent-ring" />
 <span className="lp-hero-photo-accent-dash" />
 <span className="lp-hero-photo-accent-sat" />
 </div>
 <figcaption className="lp-hero-photo-chip">
 <span className="lp-hero-photo-chip-dot" aria-hidden="true" />
 Real advisors · Real conversations
 </figcaption>
 </figure>
 </div>
 </header>

 <div className="lp-ticker" id="sectors-ticker" aria-hidden="true">
 <div className="lp-ticker-track">
 {[0, 1].map((copy) => (
 <ul key={copy}>
 {TICKER_RUN.map((item, i) => (
 <li key={`${copy}-${i}`}>{item}</li>
 ))}
 </ul>
 ))}
 </div>
 </div>

 <section className="lp-stats" id="proof" aria-label="EBS at a glance">
 <dl className="lp-wrap lp-stats-grid">
 {STATS.map((stat) => (
 <div key={stat.value} className="lp-reveal">
 <dt>{stat.value}</dt>
 <dd>{stat.label}</dd>
 </div>
 ))}
 </dl>
 </section>

 <section className="lp-section lp-section-paper lp-role-section" id="our-role" aria-labelledby="role-title">
 <div className="lp-wrap">
 <div className="lp-head lp-reveal">
 <div>
 <p className="lp-eyebrow">01 - Our role</p>
 <h2 id="role-title">
 We own the decisions.
 <br />
 <span className="lp-muted-h">Providers own the delivery.</span>
 </h2>
 </div>
 <p className="lp-lede">
 EBS is not an MSP. We sit on your side of the table setting strategy, writing requirements, and holding qualified providers accountable for engineering and support.
 </p>
 </div>
 <div className="lp-role">
 <article className="lp-card lp-card-navy lp-role-ebs lp-reveal">
 <span className="lp-deco" aria-hidden="true">
 <i className="orbit-glow" />
 <i className="orbit-ring" />
 <i className="orbit-dash" />
 <i className="orbit-sat" />
 <i className="dot-grid" />
 </span>
 <p className="lp-kicker">Advisory side</p>
 <h3>EBS owns</h3>
 <ul className="lp-checks">
 {EBS_OWNS.map((item) => <li key={item}>{item}</li>)}
 </ul>
 </article>
 <div className="lp-hub" role="img" aria-label="You, your leadership team, speaking with one voice">
 <img src={clientHub} alt="" width="158" height="86" />
 <p aria-hidden="true">One voice</p>
 </div>
 <article className="lp-card lp-card-light lp-role-providers lp-reveal">
 <span className="lp-deco" aria-hidden="true">
 <i className="lp-capsule-fill" />
 <i className="lp-capsule-line" />
 <i className="moon" />
 </span>
 <p className="lp-kicker lp-kicker-muted">Delivery side</p>
 <h3>Qualified providers own</h3>
 <ul className="lp-checks is-quiet">
 {PROVIDERS_OWN.map((item) => <li key={item}>{item}</li>)}
 </ul>
 </article>
 </div>
 </div>
 </section>

 <section className="lp-section lp-services" id="services" aria-labelledby="services-title">
 <span className="lp-capsule-fill lp-services-capsule" aria-hidden="true" />
 <span className="lp-capsule-line lp-services-capsule-line" aria-hidden="true" />
 <div className="lp-wrap">
 <div className="lp-head lp-reveal">
 <div>
 <p className="lp-eyebrow">02 - Services</p>
 <h2 id="services-title">Three ways in. <span className="lp-accent-sans">One partner.</span></h2>
 </div>
 <p className="lp-lede">
 Start with a conversation. Scale to a roadmap or an ongoing advisory cadence only when it earns its place.
 </p>
 </div>
 <div className="lp-bento">
 <article className="lp-card lp-bento-review lp-reveal">
 <p className="lp-pill">Start here · No cost</p>
 <h3>Technology<br />Strategy Review</h3>
 <p>A focused 30-45 minute conversation to clarify priorities, surface risk, and recommend the right next step - not a sales pitch.</p>
 <div className="lp-review-body">
 <ol className="lp-numbered">
 <li>Priorities &amp; constraints</li>
 <li>Renewals and risk on the horizon</li>
 <li>A clear recommended next step</li>
 </ol>
 <img className="lp-art lp-art-note" src={takingNote} alt="" width="168" height="177" loading="lazy" />
 </div>
 <a className="lp-btn lp-btn-navy" href="#contact">
 Book a Strategy Review <span aria-hidden="true">→</span>
 </a>
 </article>
 <article className="lp-card lp-card-navy lp-bento-assess lp-reveal">
 <span className="lp-deco" aria-hidden="true">
 <i className="glow" />
 <i className="layer l1" />
 <i className="layer l2" />
 <i className="layer l3" />
 <i className="node" />
 </span>
 <p className="lp-kicker">Scoped engagement</p>
 <h3>Technology Assessment &amp; Roadmap</h3>
 <p>Current-state clarity and a 12-18 month plan your board can actually follow.</p>
 <ul className="lp-tags">
 <li>Current-state map</li>
 <li>Risk register</li>
 <li>Renewal calendar</li>
 <li>12-18 mo priorities</li>
 <li>Budget view</li>
 </ul>
 </article>
 <article className="lp-card lp-card-light lp-bento-vcio lp-reveal">
 <span className="lp-deco" aria-hidden="true">
 <i className="disc" />
 <i className="arc a1" />
 <i className="arc a2" />
 </span>
 <p className="lp-kicker lp-kicker-muted">Ongoing</p>
 <h3>vCIO Advisory</h3>
 <p>Roadmap ownership, vendor governance, and quarterly business reviews.</p>
 <div className="lp-qbr" aria-hidden="true">
 {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
 <div key={q} className={q === 'Q2' ? 'is-now' : undefined}>
 <strong>{q}</strong><span>QBR</span>
 </div>
 ))}
 </div>
 </article>
 <article className="lp-card lp-card-ink lp-bento-towers lp-reveal">
 <span className="lp-deco" aria-hidden="true">
 <i className="glow" />
 <i className="bar b1" />
 <i className="bar b2" />
 <i className="bar b3" />
 </span>
 <p className="lp-kicker lp-kicker-muted">We help source</p>
 <ul className="lp-towers">
 <li className="t1">Managed IT &amp; M365<small>Tower 01</small></li>
 <li className="t2">Security &amp; Resilience<small>Tower 02</small></li>
 <li className="t3">Network &amp; Comms<small>Tower 03</small></li>
 </ul>
 </article>
 </div>
 <p className="lp-footnote">
 Compensation disclosure: EBS may be compensated by providers or distributors when clients purchase certain solutions. Evaluations follow your requirements across a broad provider ecosystem.
 </p>
 </div>
 </section>

 <section className="lp-section lp-section-dark lp-serve" id="who-we-serve" aria-labelledby="serve-title">
 <span className="lp-glow lp-serve-glow" aria-hidden="true" />
 <LandingRings className="lp-serve-rings" rings={SERVE_RINGS} />
 <div className="lp-wrap">
 <div className="lp-head lp-reveal">
 <div>
 <p className="lp-eyebrow">03 - Who we serve</p>
 <h2 id="serve-title">
 Real operational complexity.
 <br />
 <span className="lp-accent">No CIO on payroll.</span>
 </h2>
 </div>
 <p className="lp-lede">
 Typically 20-500 employees, meaningful technology spend, and leadership teams that need answers they can defend.
 </p>
 </div>
 <div className="lp-sectors">
 {SECTORS.map((sector, index) => (
 <article key={sector.title} className={`lp-sector is-${sector.tone} lp-reveal`}>
 <span className="lp-sector-num" aria-hidden="true">0{index + 1}</span>
 <span className="lp-sector-motif" aria-hidden="true"><i /><i /><i /><i /></span>
 <span className={`lp-deco is-${sector.motif}`} aria-hidden="true">
 <i className="glow" />
 {SECTOR_SHAPES[sector.motif].map((shape) => <i key={shape} className={shape} />)}
 </span>
 <h3>{sector.title}</h3>
 <p>{sector.body}</p>
 <ul>
 {sector.tags.map((tag) => <li key={tag}>{tag}</li>)}
 </ul>
 </article>
 ))}
 </div>
 </div>
 </section>

 <HowWeWork />

 <section className="lp-section lp-section-deep" id="about" aria-labelledby="about-title">
 <div className="lp-wrap lp-about">
 <div className="lp-reveal">
 <p className="lp-eyebrow">05 - Who we are</p>
 <h2 id="about-title">
 Senior judgment.
 <br />
 <span className="lp-muted-h">No vendor agenda.</span>
 </h2>
 <p className="lp-about-body">
 EBS is led by a technology executive with more than 20 years of IT experience - modernizing infrastructure, strengthening cybersecurity, managing technology vendors, and aligning IT investment with business priorities. Every engagement starts with your goals, risks, and operating realities - not with a product.
 </p>
 <ul className="lp-chips-dark">
 <li>20+ years IT leadership</li>
 <li>Healthcare · nonprofit · manufacturing</li>
 <li>Advisory, not an MSP</li>
 </ul>
 </div>
 <div className="lp-about-side">
 <figure className="lp-quote lp-reveal">
 <p className="lp-kicker">Our role is simple</p>
 <blockquote>
 <p>“We help organizations decide what technology they need, why they need it, who should deliver it, and how to make sure the investment produces the intended result.”</p>
 </blockquote>
 <figcaption>
 <cite>Elm Business Solutions</cite>
 <span>Technology advisory &amp; sourcing · Middletown, CT</span>
 </figcaption>
 </figure>
 <Link className="lp-case lp-reveal" to="/case-studies/twenty-crm">
 <span>
 <small>Case study · Reference implementation</small>
 How we run our own stack: a production Twenty CRM, built and governed by EBS.
 </span>
 <i aria-hidden="true">→</i>
 </Link>
 </div>
 </div>
 </section>

 <FitCheck selected={fitSelected} onToggle={toggleFit} />

 <LandingFaq />

 <section className="lp-cta" id="contact" aria-labelledby="cta-title">
 <div className="lp-cta-glow" aria-hidden="true" />
 <LandingRings className="lp-cta-ringset" rings={CTA_RINGS} core={{ r: 8, color: '#00afd7' }} />
 <div className="lp-wrap lp-cta-grid">
 <div className="lp-cta-copy lp-reveal">
 <p className="lp-eyebrow">08 - Start here</p>
 <h2 id="cta-title">
 Your next technology
 <br />
 decision deserves
 <br />
 <span className="lp-cta-accent">
 a second opinion.
 <img src={markHighlighter} alt="" aria-hidden="true" />
 </span>
 </h2>
 <ul className="lp-expect">
 {EXPECTATIONS.map((item) => (
 <li key={item.title}>
 <img src={item.icon} alt="" width="22" height="22" />
 <span>
 <strong>{item.title}</strong>
 {item.detail}
 </span>
 </li>
 ))}
 </ul>
 <p className="lp-eyebrow lp-next-title">What happens next</p>
 <ol className="lp-next">
 {NEXT_STEPS.map((step) => (
 <li key={step.title}>
 <strong>{step.title}</strong>
 <span>{step.detail}</span>
 </li>
 ))}
 </ol>
 <p className="lp-prefer">
 Prefer email? <a href={`mailto:${email}`}>{email} <span aria-hidden="true">→</span></a>
 </p>
 </div>
 <LandingForm fitSignals={fitChips} />
 </div>
 </section>

 <LandingFooter />
 </div>
 );
};

export default LandingPage;
